import { useEffect, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Divider from '@mui/material/Divider'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import IconButton from '@mui/material/IconButton'
import axios from 'axios'
import api from 'services/api'
import { ADMIN_API_URL } from 'const'
import {
  TextArea,
  Header,
  Text,
  ExpandableTitles,
  ExpandableTranscript,
  ExpandableText,
  CopyResultsButton,
  formatTestExport,
} from './Bits'
import ModelAndThinkingConfig from './ThinkingConfig'
import { usePresetSide, PresetSelector, SchemaDescEditor } from './PresetConfig'

const MomentCard = ({ moment, audioRef, playingRank, onPlay, onStop }) => {
  const isPlaying = playingRank === moment.rank

  const handleClick = () => {
    if (isPlaying) {
      onStop()
    } else {
      onPlay(moment)
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        p: 2,
        mb: 2,
        borderLeft: moment.rank === 1 ? '3px solid' : 'none',
        borderColor: 'primary.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ fontWeight: 700, mr: 1 }}>#{moment.rank}</Box>
        <Box sx={{ fontSize: '0.85em', color: 'text.secondary' }}>
          {moment.start_s.toFixed(2)}s &ndash; {moment.end_s.toFixed(2)}s (
          {(moment.end_s - moment.start_s).toFixed(1)}s)
        </Box>
        {audioRef && (
          <IconButton size="small" onClick={handleClick} sx={{ ml: 1 }}>
            {isPlaying ? (
              <StopIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
      <Box sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
        <ExpandableText
          text={moment.transcript_slice}
          maxLength={120}
          fontSize="0.9em"
        />
      </Box>
      <Box sx={{ fontSize: '0.85em', color: 'text.secondary' }}>
        {moment.why}
      </Box>
    </Box>
  )
}

const AutoTrimTest = () => {
  const [sourceId, setSourceId] = useState('')
  const [presets, setPresets] = useState([])
  const side = usePresetSide(presets)
  const [isTesting, setIsTesting] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [model, setModel] = useState('gemini-2.5-flash-lite')
  const [thinkingConfig, setThinkingConfig] = useState({})
  const [numCandidates, setNumCandidates] = useState(3)
  const [audioBlobUrl, setAudioBlobUrl] = useState(null)
  const [playingRank, setPlayingRank] = useState(null)
  const audioRef = useRef(null)
  const endTimeRef = useRef(null)

  const stopAudio = useCallback(() => {
    endTimeRef.current = null
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setPlayingRank(null)
  }, [])

  const playMoment = useCallback((moment) => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    endTimeRef.current = moment.end_s
    audio.currentTime = moment.start_s
    const p = audio.play()
    if (p) p.catch(() => {})
    setPlayingRank(moment.rank)
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    let rafId = null
    const checkEnd = () => {
      if (
        endTimeRef.current != null &&
        audio.currentTime >= endTimeRef.current
      ) {
        audio.pause()
        endTimeRef.current = null
        setPlayingRank(null)
      } else if (endTimeRef.current != null) {
        rafId = requestAnimationFrame(checkEnd)
      }
    }
    const onPlay = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(checkEnd)
    }
    const onPause = () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    return () => {
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [audioBlobUrl])

  useEffect(() => {
    return () => {
      if (audioBlobUrl) {
        URL.revokeObjectURL(audioBlobUrl)
      }
    }
  }, [audioBlobUrl])

  useEffect(() => {
    api.autoTrim
      .getPrompt()
      .then((data) => {
        setPresets(data.presets || [])
      })
      .catch((err) => {
        setError(err.message || 'Failed to load presets')
      })
  }, [])

  const handleTest = useCallback(() => {
    if (!sourceId.trim()) return
    setIsTesting(true)
    setResult(null)
    setError(null)

    if (audioBlobUrl) {
      URL.revokeObjectURL(audioBlobUrl)
      setAudioBlobUrl(null)
    }

    const apiParams = side.getApiParams()
    api.autoTrim
      .testAutoTrim(sourceId.trim(), {
        ...apiParams,
        model,
        numCandidates,
        ...thinkingConfig,
      })
      .then((data) => {
        setResult(data)
        return axios.get(
          `${ADMIN_API_URL}/reclips/${sourceId.trim()}/audio?trim=false`,
          { responseType: 'blob' }
        )
      })
      .then((res) => {
        const url = URL.createObjectURL(res.data)
        setAudioBlobUrl(url)
      })
      .catch((err) => {
        setError(err.message || 'An error occurred')
      })
      .finally(() => {
        setIsTesting(false)
      })
  }, [sourceId, side, audioBlobUrl, model, thinkingConfig, numCandidates])

  const handleClear = useCallback(() => {
    setSourceId('')
    setResult(null)
    setError(null)
    if (audioBlobUrl) {
      URL.revokeObjectURL(audioBlobUrl)
      setAudioBlobUrl(null)
    }
  }, [audioBlobUrl])

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: 800, height: '100%', overflow: 'auto' }}>
        <Box sx={{ fontSize: '1.2em', fontWeight: 700, mb: 1 }}>
          Test Auto Trim
        </Box>
        <Text>
          Enter a source ID to fetch its raw transcript and run the auto_trim
          Gemini prompt. You can find source IDs on the{' '}
          <Box
            sx={{
              display: 'inline',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() => window.open('/search', '_blank')}
          >
            search
          </Box>{' '}
          page.
        </Text>

        <Header text="Source ID" />
        <Box
          sx={{
            '& input': {
              backgroundColor: 'background.paper',
              width: '100%',
              display: 'block',
              border: 0,
              p: 1.5,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'divider',
              borderRadius: '8px',
              fontSize: '0.95em',
              '&:focus': { borderColor: 'white', outline: 'none' },
            },
          }}
        >
          <input
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            placeholder="e.g. 123456AA-BC78-9DE0-1F2G-HI3456789JKL"
          />
        </Box>

        <PresetSelector
          presets={presets}
          presetKey={side.presetKey}
          onPresetChange={side.selectPreset}
          minS={side.minS}
          maxS={side.maxS}
          onMinChange={side.setMinS}
          onMaxChange={side.setMaxS}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
          <Header text="System Prompt" mt={0} />
          {side.promptModified && (
            <Button
              size="small"
              onClick={side.resetPrompt}
              sx={{ ml: 2, fontSize: '0.75em', textTransform: 'none' }}
            >
              Reset to Default
            </Button>
          )}
        </Box>
        <TextArea value={side.prompt} onChange={side.setPrompt} rows={8} />

        <SchemaDescEditor
          schemaDescs={side.schemaDescs}
          originalSchemaDescs={side.originalSchemaDescs}
          onSchemaDescChange={side.setSchemaDesc}
          onResetSchema={side.resetSchema}
        />

        <ModelAndThinkingConfig
          model={model}
          onModelChange={setModel}
          onChange={setThinkingConfig}
          numCandidates={numCandidates}
          onNumCandidatesChange={setNumCandidates}
        />

        <Box
          sx={{
            mt: 2,
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={!sourceId && !result}
          >
            Clear
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleTest}
            disabled={!sourceId.trim()}
            loading={isTesting}
          >
            Test
          </LoadingButton>
        </Box>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Header text="Error" />
            <Box
              sx={{
                backgroundColor: 'background.paper',
                p: 2,
                borderRadius: '8px',
                color: 'error.main',
                whiteSpace: 'pre-wrap',
                fontSize: '0.9em',
              }}
            >
              {error}
            </Box>
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" sx={{ mx: 4 }} />

      <Box sx={{ width: 800, height: '100%', overflow: 'auto' }}>
        {!result && !isTesting && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Results will appear here after running a test.
          </Box>
        )}

        {isTesting && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Running auto trim...
          </Box>
        )}

        {result && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <CopyResultsButton getText={() => formatTestExport(result)} />
            </Box>
            <Box sx={{ fontSize: '0.9em', color: 'text.secondary', mb: 2 }}>
              <Box>
                Completed in {(result.latency_ms / 1000).toFixed(1)}s &middot;{' '}
                {result.word_count} words &middot; {result.duration_s}s audio
                &middot; {result.model_used}
              </Box>
              <Box sx={{ mt: 1 }}>
                Tokens: {result.prompt_tokens?.toLocaleString()} prompt &middot;{' '}
                {result.completion_tokens?.toLocaleString()} completion &middot;{' '}
                {result.thinking_tokens?.toLocaleString()}/
                {result.thinking_budget?.toLocaleString()} thinking
              </Box>
            </Box>

            {audioBlobUrl && (
              <Box sx={{ mb: 2 }}>
                <audio
                  ref={audioRef}
                  src={audioBlobUrl}
                  preload="auto"
                  style={{ width: '100%', height: 32 }}
                  controls
                />
              </Box>
            )}

            {result.auto_emojis?.length > 0 && (
              <>
                <Header text="Emojis" />
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    p: 2,
                    mb: 2,
                    fontSize: '2em',
                    letterSpacing: '0.3em',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  {result.auto_emojis.join(' ')}
                </Box>
              </>
            )}

            {result.best_quote && (
              <>
                <Header text="Best Quote" />
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    p: 2,
                    mb: 2,
                    fontStyle: 'italic',
                    fontSize: '0.95em',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  &ldquo;{result.best_quote}&rdquo;
                </Box>
              </>
            )}

            {result.animation_context && (
              <>
                <Header text="Animation Context" />
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    p: 1.5,
                    mb: 2,
                    fontSize: '0.9em',
                    borderLeft: '3px solid',
                    borderColor: 'secondary.main',
                  }}
                >
                  {result.animation_context}
                </Box>
              </>
            )}

            {result.titles?.length > 0 && (
              <>
                <Header text="Titles" />
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    p: 2,
                    mb: 2,
                  }}
                >
                  <ExpandableTitles titles={result.titles} />
                </Box>
              </>
            )}

            <Header text="Moments" />
            {result.moments.map((m) => (
              <MomentCard
                key={m.rank}
                moment={m}
                audioRef={audioRef}
                playingRank={playingRank}
                onPlay={playMoment}
                onStop={stopAudio}
              />
            ))}

            <Box sx={{ mt: 2 }}>
              <ExpandableTranscript
                transcript={result.punctuated_text}
                label="full transcript"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AutoTrimTest
